export function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function groupDateRanges(dates: string[]): string[] {
  // Sort the dates in ascending order
  dates.sort();

  const ranges: string[] = [];
  let start: string = dates[0];
  let end: string = dates[0];

  for (let i = 1; i < dates.length; i++) {
    const currentDate: Date = new Date(dates[i]);
    const previousDate: Date = new Date(dates[i - 1]);

    // Check if the current date is consecutive to the previous one
    const timeDiff: number =
      (currentDate.getTime() - previousDate.getTime()) / (1000 * 3600 * 24);
    if (timeDiff === 1) {
      end = dates[i]; // Extend the range
    } else {
      // If not consecutive, push the range to the result array
      if (start !== end) {
        ranges.push(`${start} - ${end}`);
      } else {
        ranges.push(start);
      }
      start = dates[i]; // Start a new range
      end = dates[i];
    }
  }

  // Push the last range to the result array
  if (start !== end) {
    ranges.push(`${start} - ${end}`);
  } else {
    ranges.push(start);
  }

  return ranges;
}

interface DateWithVotes {
  date: string;
  users: string[];
}

interface GroupedDateRange {
  range: string;
  users: string[];
  start: Date;
  end: Date;
}

function sortByRangeAndUsers(a: GroupedDateRange, b: GroupedDateRange): number {
  const durationA = getDaysInRange(a.range);
  const durationB = getDaysInRange(b.range);

  if (b.users.length === a.users.length) {
    return durationB - durationA;
  }

  return b.users.length - a.users.length;
}

export function groupDateRangesWithUsers(
  dateVotes: DateWithVotes[],
): GroupedDateRange[] {
  if (!dateVotes.length) {
    return [];
  }

  // Sort the date votes by date
  dateVotes.sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateA.getTime() - dateB.getTime();
  });

  const groupedRanges: GroupedDateRange[] = [];
  let start: string = dateVotes[0].date;
  let end: string = dateVotes[0].date;
  let usersInGroup: string[] = dateVotes[0].users.slice(); // Copy the array to avoid modifying original

  for (let i = 1; i < dateVotes.length; i++) {
    const currentDate = new Date(dateVotes[i].date);
    const previousDate = new Date(dateVotes[i - 1].date);
    const currentUserVotes = dateVotes[i].users;
    const previousUserVotes = dateVotes[i - 1].users;

    const timeDiff =
      (currentDate.getTime() - previousDate.getTime()) / (1000 * 3600 * 24);

    // Check if the current date is consecutive to the previous one and if users voted the same
    const sameUsersVoted =
      JSON.stringify(currentUserVotes.sort()) ===
      JSON.stringify(previousUserVotes.sort());
    if (timeDiff === 1 && sameUsersVoted) {
      end = dateVotes[i].date; // Extend the range
      usersInGroup = usersInGroup.filter((user) =>
        currentUserVotes.includes(user),
      ); // Update users in the group
    } else {
      // If not consecutive or users are different, push the range and users to the result array
      if (start === end) {
        groupedRanges.push({
          range: start,
          users: usersInGroup,
          start: parseLocalDate(start),
          end: parseLocalDate(end),
        });
      } else {
        groupedRanges.push({
          range: `${start} - ${end}`,
          users: usersInGroup,
          start: parseLocalDate(start),
          end: parseLocalDate(end),
        });
      }
      start = dateVotes[i].date; // Start a new range
      end = dateVotes[i].date;
      usersInGroup = dateVotes[i].users.slice(); // Copy the array to avoid modifying original
    }
  }

  // Push the last range and users to the result array
  if (start === end) {
    groupedRanges.push({
      range: start,
      users: usersInGroup,
      start: parseLocalDate(start),
      end: parseLocalDate(end),
    });
  } else {
    groupedRanges.push({
      range: `${start} - ${end}`,
      users: usersInGroup,
      start: parseLocalDate(start),
      end: parseLocalDate(end),
    });
  }

  return groupedRanges.sort(sortByRangeAndUsers);
}

export function groupedRangeToDates(range: string): string[] {
  const dates: string[] = [];

  const parts = range.split(" - ");
  const startDate = new Date(parts[0]);
  const endDate = parts.length === 2 ? new Date(parts[1]) : new Date(startDate);

  for (
    let currentDate = startDate;
    currentDate <= endDate;
    currentDate.setDate(currentDate.getDate() + 1)
  ) {
    dates.push(currentDate.toISOString().slice(0, 10));
  }

  return dates;
}

export function getDaysDiff(startDate: Date, endDate: Date): number {
  // Calculate the difference in milliseconds between end and start dates
  const diffInMilliseconds = endDate.getTime() - startDate.getTime();

  // Convert milliseconds to days and add 1 to include both start and end dates
  const diffInDays = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24)) + 1;

  return diffInDays;
}

export function getDaysInRange(range: string): number {
  const [startDateString, endDateString] = range.split(" - ");
  const startDate = new Date(startDateString);
  const endDate = endDateString ? new Date(endDateString) : startDate;

  return getDaysDiff(startDate, endDate);
}

export function getDaysInStartEnd(
  startDate: Date,
  endDate: Date = startDate,
): number {
  return getDaysDiff(startDate, endDate);
}
